// frontend/src/features/livestock/OrganismPhotoViewer.tsx
// One organism photo full screen (livestock compare design 8.3, plan 5.3): the
// organism's TankShotViewer. The photo shows as taken, with its date and note;
// Line up against the organism's reference photo, Reset line up behind a
// confirm (on the reference too, when it still has a crop of its own), and
// Make reference. "Lined up" and "Shared in N links" badges.
// Edit and Delete stay on the timeline card's menu.

import { useState } from 'react';
import { Badge, Button, Group, Modal, Stack, Text } from '@mantine/core';
import { IconFocusCentered, IconFrameOff, IconLink, IconTarget } from '@tabler/icons-react';
import { timelineApi } from '@/api/timeline';
import { useSetOrganismPhotoFraming, useSetOrganismReference } from '@/hooks/useLivestockPhotos';
import type { Livestock } from '@/types/livestock';
import type { PhotoFraming } from '@/types/photos';
import type { LargePhoto } from '@/types/tankPhotos';
import type { TimelineEntry } from '@/types/timeline';
import { PhotoLineUpModal } from './LineUpModal';
import { linedUpToEarlierReference, lineUpMode, organismReferenceOf } from './lineUpReference';
import { LIGHTBOX_BAR_BUTTON, LIGHTBOX_Z_INDEX, PhotoLightbox } from './PhotoLightbox';
import { formatTakenAt } from './photoInboxFormat';
import { ORGANISM_WORDS } from './photoSubject';

export interface OrganismPhotoViewerProps {
  tankId: string;
  /** The organism, from its detail read (its reference photo and the ghost's URL). */
  livestock: Livestock;
  /** The open entry (one with a photo); null closes the viewer. */
  entry: TimelineEntry | null;
  onClose: () => void;
}

/** The reset confirm opens over the full-screen lightbox. */
const OVER_LIGHTBOX = LIGHTBOX_Z_INDEX + 50;

/** "Shared in 2 links" */
export function sharedInLinks(count: number): string {
  return `Shared in ${count} ${count === 1 ? 'link' : 'links'}`;
}

export function OrganismPhotoViewer({
  tankId,
  livestock,
  entry,
  onClose,
}: OrganismPhotoViewerProps): JSX.Element {
  const [liningUp, setLiningUp] = useState(false);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [confirmingReference, setConfirmingReference] = useState(false);
  // Another photo, or none: nothing opened for the previous one carries over
  const openId = entry?.id ?? null;
  const [flagsFor, setFlagsFor] = useState(openId);
  if (flagsFor !== openId) {
    setFlagsFor(openId);
    setLiningUp(false);
    setConfirmingReset(false);
    setConfirmingReference(false);
  }
  const framingChange = useSetOrganismPhotoFraming(tankId, livestock.id);
  const referenceChange = useSetOrganismReference(tankId, livestock.id);
  const reference = organismReferenceOf(livestock);
  const photo = entry && (entry.photo_url || entry.photo_display_url) ? entry : null;
  const isReference = !!photo && lineUpMode(photo.id, reference) === 'reference';
  const framing = photo?.framing ?? null;
  const shareCount = photo?.share_link_count ?? 0;

  const close = (): void => {
    setLiningUp(false);
    setConfirmingReset(false);
    setConfirmingReference(false);
    onClose();
  };

  // A new reference resets every line up of the organism (the server clears
  // them: docs/plans/2026-10-30-organism-reference-reset-and-as-taken.md, 1):
  // asked first. The first reference needs no asking.
  const askMakeReference = (): void => {
    if (!photo) return;
    if (reference) setConfirmingReference(true);
    else referenceChange.mutate(photo.id);
  };

  const confirmMakeReference = async (): Promise<void> => {
    if (!photo) return;
    try {
      await referenceChange.mutateAsync(photo.id);
      setConfirmingReference(false);
    } catch {
      // Notified by the hook; the confirm stays open.
    }
  };

  const confirmReset = async (): Promise<void> => {
    if (!photo) return;
    try {
      await framingChange.mutateAsync({ id: photo.id, framing: null });
      setConfirmingReset(false);
    } catch {
      // Notified by the hook; the confirm stays open.
    }
  };

  const saveFraming = async (id: number, next: PhotoFraming): Promise<void> => {
    await framingChange.mutateAsync({ id, framing: next });
  };
  const makeReference = async (id: number): Promise<void> => {
    await referenceChange.mutateAsync(id);
  };

  const caption = photo ? (
    <Stack gap={2}>
      <Text size="sm" fw={500} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatTakenAt(photo.entry_date)}
      </Text>
      {photo.entry_text && (
        <Text size="sm" c="gray.4" lineClamp={3} style={{ whiteSpace: 'pre-line' }}>
          {photo.entry_text}
        </Text>
      )}
      {isReference && (
        <Text size="sm" c="gray.4" data-testid="reference-note">
          This is the reference photo. Other photos are lined up to match it.
        </Text>
      )}
      {isReference && framing && (
        <Text size="sm" c="gray.4" data-testid="cropped-reference-note">
          It is still cropped by an earlier line up, so compare leaves part of it out. Reset its
          line up to use the whole photo.
        </Text>
      )}
      {!isReference && linedUpToEarlierReference(framing, reference) && (
        <Text size="sm" c="gray.4" data-testid="earlier-reference-note">
          Lined up to an earlier reference photo. Line up again to match the current one.
        </Text>
      )}
    </Stack>
  ) : null;

  const badges =
    framing || shareCount > 0 ? (
      <>
        {framing && (
          <Badge variant="light" color="teal" leftSection={<IconFocusCentered size={12} />} mr="xs">
            Lined up
          </Badge>
        )}
        {shareCount > 0 && (
          <Badge
            variant="light"
            color="gray"
            leftSection={<IconLink size={12} />}
            mr="xs"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {sharedInLinks(shareCount)}
          </Badge>
        )}
      </>
    ) : null;

  return (
    <>
      <PhotoLightbox
        opened={!!photo}
        onClose={close}
        photoUrl={photo ? (photo.photo_url ?? photo.photo_display_url) : null}
        photoKey={photo?.id}
        loadLarge={
          photo
            ? (): Promise<LargePhoto | null> => timelineApi.large(tankId, livestock.id, photo.id)
            : undefined
        }
        alt={photo ? `Photo, ${formatTakenAt(photo.entry_date)}` : undefined}
        caption={caption}
        badges={badges}
        actions={
          photo && (!isReference || framing) ? (
            <>
              {!isReference && (
                <Button
                  {...LIGHTBOX_BAR_BUTTON}
                  leftSection={<IconFocusCentered size={16} />}
                  onClick={() => setLiningUp(true)}
                >
                  Line up
                </Button>
              )}
              {framing && (
                <Button
                  {...LIGHTBOX_BAR_BUTTON}
                  leftSection={<IconFrameOff size={16} />}
                  onClick={() => setConfirmingReset(true)}
                >
                  Reset line up
                </Button>
              )}
              {!isReference && (
                <Button
                  {...LIGHTBOX_BAR_BUTTON}
                  leftSection={<IconTarget size={16} />}
                  onClick={askMakeReference}
                  loading={referenceChange.isPending && !confirmingReference}
                >
                  Make reference
                </Button>
              )}
            </>
          ) : null
        }
      />

      <PhotoLineUpModal
        shot={liningUp ? photo : null}
        reference={reference}
        words={ORGANISM_WORDS}
        onSave={saveFraming}
        onMakeReference={makeReference}
        onClose={() => setLiningUp(false)}
      />

      <Modal
        opened={confirmingReference && !!photo}
        onClose={() => setConfirmingReference(false)}
        title="Make this the reference photo?"
        size="sm"
        centered
        zIndex={OVER_LIGHTBOX}
      >
        <Stack gap="md">
          <Text size="sm">
            Every lined-up photo of this organism will be reset, this one included, so they can be
            lined up again against the whole of this photo. Shared links show those photos as taken
            until then.
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => setConfirmingReference(false)}
              disabled={referenceChange.isPending}
            >
              Cancel
            </Button>
            <Button onClick={() => void confirmMakeReference()} loading={referenceChange.isPending}>
              Make reference
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={confirmingReset && !!photo}
        onClose={() => setConfirmingReset(false)}
        title="Reset this line up?"
        size="sm"
        centered
        zIndex={OVER_LIGHTBOX}
      >
        <Stack gap="md">
          <Text size="sm">
            The photo shows as taken again in Compare. The photo itself is never changed.
          </Text>
          {isReference && (
            <Text size="sm" data-testid="reset-reference-warning">
              Photos already lined up to it were matched to its cropped view, so line them up again
              afterwards.
            </Text>
          )}
          <Group justify="flex-end" gap="xs">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => setConfirmingReset(false)}
              disabled={framingChange.isPending}
            >
              Cancel
            </Button>
            <Button onClick={() => void confirmReset()} loading={framingChange.isPending}>
              Reset
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
